import { Address } from 'cpnl-common';
import { DataOverview, DefinitionList, DefinitionListItem } from '@cp-shared-19/frontend-ui';
import { Button, Heading } from '@vwfs-bronson/bronson-react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { EditStatus, NotificationForEditStatus } from './NotificationForEditStatus';
import { changeAddressPath } from '../../../navigation/paths';

export const AddressView: React.FC<{
    address?: Address;
    onEdit: (addressType: string, event: MouseEvent) => void;
    addressType?: string;
    editStatus: EditStatus;
}> = ({ address, onEdit, editStatus, addressType }) => {
    const { t } = useTranslation(['my-profile', 'change-address']);

    const showVisit = address?.visitAddressCountry || address?.visitAddressLine1 || address?.visitAddressLine2;
    const showPostal = address?.postalAddressCountry || address?.postalAddressLine1 || address?.postalAddressLine2;
    const showButton = !showVisit && showPostal ? true : !!(showVisit && !showPostal);
    const addAddressButtonLabel = !showVisit
        ? t('change-address:add-button-label.visiting-address')
        : t('change-address:add-button-label.postal-address');

    const visitAddressItems: DefinitionListItem[] = [
        {
            label: t('address.visit-address-line1'),
            value: address?.visitAddressLine1,
        },
        {
            label: t('address.visit-address-line2'),
            value: address?.visitAddressLine2,
        },
        {
            label: t('address.visit-address-country'),
            value: address?.visitAddressCountry,
        },
    ].filter((item) => item.value);

    const postalAddressItems: DefinitionListItem[] = [
        {
            label: t('address.postal-address-line1'),
            value: address?.postalAddressLine1,
        },
        {
            label: t('address.postal-address-line2'),
            value: address?.postalAddressLine2,
        },
        {
            label: t('address.postal-address-country'),
            value: address?.postalAddressCountry,
        },
    ].filter((item) => item.value);

    const urlHref = changeAddressPath();

    return (
        <>
            {editStatus !== EditStatus.STARTED && (
                <Heading level={2} className={'u-mt'}>
                    {t('address.sub-headline')}
                </Heading>
            )}
            {showVisit && (
                <DataOverview
                    href={urlHref}
                    title={t('address.headline-personal-visiting-address')}
                    buttonLabel={t('change-address:edit-button.label')}
                    buttonProps={{
                        onClick: (event: MouseEvent): void => onEdit('Visit', event),
                        'aria-label': t('change-address:edit-button.aria-label'),
                        title: t('change-address:edit-button.title'),
                    }}
                    titleLevel={3}
                >
                    <DefinitionList split={true} list={visitAddressItems} />
                    {addressType === 'Visit' && <NotificationForEditStatus editStatus={editStatus} />}
                </DataOverview>
            )}
            {showPostal && (
                <DataOverview
                    href={urlHref}
                    title={t('address.headline-personal-postal-address')}
                    buttonLabel={t('change-address:edit-button.label')}
                    buttonProps={{
                        onClick: (event: MouseEvent): void => onEdit('Postal', event),
                        'aria-label': t('change-address:edit-button.aria-label'),
                        title: t('change-address:edit-button.title'),
                    }}
                    titleLevel={3}
                >
                    <DefinitionList split={true} list={postalAddressItems} />
                    {addressType === 'Postal' && <NotificationForEditStatus editStatus={editStatus} />}
                </DataOverview>
            )}
            {!showVisit && addressType === 'Visit' && editStatus !== 'NOT_PERFORMED' && (
                <DataOverview
                    href={urlHref}
                    title={t('address.headline-personal-visiting-address')}
                    buttonLabel={t('change-address:edit-button.label')}
                    buttonProps={{
                        onClick: (event: MouseEvent): void => onEdit('Visit', event),
                        'aria-label': t('change-address:edit-button.aria-label'),
                        title: t('change-address:edit-button.title'),
                    }}
                >
                    <NotificationForEditStatus editStatus={editStatus} />
                </DataOverview>
            )}
            {!showPostal && addressType === 'Postal' && editStatus !== 'NOT_PERFORMED' && (
                <DataOverview
                    href={urlHref}
                    title={t('address.headline-personal-postal-address')}
                    buttonLabel={t('change-address:edit-button.label')}
                    buttonProps={{
                        onClick: (event: MouseEvent): void => onEdit('Postal', event),
                        'aria-label': t('change-address:edit-button.aria-label'),
                        title: t('change-address:edit-button.title'),
                    }}
                >
                    <NotificationForEditStatus editStatus={editStatus} />
                </DataOverview>
            )}
            {showButton && editStatus !== EditStatus.STARTED && (
                <DataOverview>
                    <Button
                        link
                        element="a"
                        small
                        className="u-pb-small"
                        icon="semantic-forward"
                        iconReversed
                        onClick={(event: MouseEvent): void => onEdit(!showVisit ? 'Visit' : 'Postal', event)}
                    >
                        {addAddressButtonLabel}
                    </Button>
                </DataOverview>
            )}
        </>
    );
};
